import AppCommandBar from 'app/AppCommandBar'
import { AutoStack } from 'components'
import { DocumentGenerator } from 'components/DocumentGenerator/documentGenerator'
import { Formik, FormikActions, FormikContext } from 'formik'
import { Stack } from 'forms/FormStack'
import {
  ProvingDetailsInternalMemoForm,
  ProvingDetailsInternalMemoFormValues,
} from 'forms/ProvingDetailsForm/flows/ProvingDetailsInternalMemoForm'
import { omitDeep } from 'helpers'
import { useCommand } from 'hooks/useCommand'
import { useCurrentProving } from 'hooks/useCurrentProving'
import { FileTemplateType } from 'model'
import { Label } from 'office-ui-fabric-react'
import * as React from 'react'
import { useTranslation } from 'react-i18next'
import { useUpdateProvingMutation } from './hooks/index'
import { Page, Section } from './Layout'

const InternalMemoFormWrapper: React.FC<{
  formik: FormikContext<any>
}> = ({ formik, children }) => {
  const { t } = useTranslation(['ProvingDetailsView', 'common'])

  useCommand(
    {
      key: 'save',
      text: t('action.save'),
      iconProps: { iconName: 'Save' },
      priority: 0,
      disabled: formik.isSubmitting || !formik.isValid || !formik.dirty,
      onClick: () => formik.submitForm(),
    },
    [formik.submitForm, formik.isSubmitting, formik.isValid, formik.dirty]
  )
  return <>{children}</>
}

export const PageFlows: React.FC = () => {
  const { t } = useTranslation()
  const [proving, refetch] = useCurrentProving()
  const [updateProving] = useUpdateProvingMutation()

  const handleSubmit = React.useCallback(
    async (
      values: ProvingDetailsInternalMemoFormValues,
      actions: FormikActions<ProvingDetailsInternalMemoFormValues>
    ) => {
      actions.setSubmitting(true)

      await updateProving({
        variables: {
          id: proving.id,
          update: values,
        },
      })

      actions.setSubmitting(false)
      refetch()
    },
    [proving, refetch, updateProving]
  )

  return (
    <Page>
      <AppCommandBar />
      <Section label={t('section.flows.header')}>
        <Formik
          onSubmit={handleSubmit}
          initialValues={{
            internalMemo: omitDeep(proving.internalMemo, '__typename'),
          }}
        >
          {(formik) => (
            <InternalMemoFormWrapper formik={formik}>
              <ProvingDetailsInternalMemoForm />
            </InternalMemoFormWrapper>
          )}
        </Formik>
      </Section>
      <Section label={t('section.flows.travelSheet')}>
        <AutoStack tokens={{ childrenGap: 8 }}>
          <Stack.Item className="composite-field">
            <Label>{t('field.arrivalSheet.label')}</Label>
            <DocumentGenerator
              templateType={FileTemplateType.ArrivalSheet}
              fieldPath={'arrivalSheet'}
              getUpdate={(doc) => ({
                arrivalSheet: doc,
              })}
            />
          </Stack.Item>
        </AutoStack>
      </Section>
      <Section label={t('section.flows.applicationForm')}>
        <AutoStack tokens={{ childrenGap: 8 }}>
          <Stack.Item className="composite-field">
            <Label>{t('field.applicationForm.label')}</Label>
            <DocumentGenerator
              templateType={FileTemplateType.ApplicationForm}
              fieldPath={'applicationForm'}
              getUpdate={(doc) => ({
                applicationForm: doc,
              })}
            />
          </Stack.Item>
        </AutoStack>
      </Section>
      {/* <Section label={t('section.debug.header')}>
        <pre>{JSON.stringify(proving, null, 2)}</pre>
      </Section> */}
    </Page>
  )
}
